import { Injectable, Injector, inject, WritableSignal } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { first } from 'rxjs/operators';
import { EvCalculationService } from '../core/ev-calculation.service';
import { WaypointService } from '../core/waypoint.service';
import { MapService } from '../map/map.service';
import { MapRangeDrawerService, RangeFit } from '../map/map-range-drawer.service';
import { MapRouteDrawerService } from '../map/map-route-drawer.service';
import { NavigationMotionControllerService } from '../map/navigation-motion-controller.service';
import { DataParserService } from '../core/data-parser.service';
import { WebviewService } from '../platform/webview.service';
import { SettingsService } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { NavigationService } from '../core/navigation.service';
import { CalculationError, CalculationIntent, CalculationState } from '../../models/ev-calculation-error-mapper';
import { FollowModeState } from '../../models/follow-mode-states';
import { ConfirmationDialogComponent } from '../../components/modals/confirmation-dialog/confirmation-dialog.component';
import { SubscriptionRequiredDialogComponent } from '../../components/modals/subscriptions-dialog/subscription-required-dialog/subscription-required-dialog.component';
import { MapComponent } from '../../components/map/map.component';
import { VehiclePanelComponent } from '../../components/ui-panels/vehicle-panel/vehicle-panel.component';
import { RangeParams } from '../../models/range-params';
import { LatLng } from '../../models/latlng';
import { Route, WaypointAddress } from '../../models/route';
import { Range } from '../../models/range';
import { DisplayMode } from '../../models/display-mode';
import { ChargeState } from '../../models/charge-state';
import { BetterRouteState } from '../../models/better-route';
import { WindElement } from '../../models/wind';
import { VehicleService } from '../vehicle/vehicle.service';
import { LocationService } from '../platform/location.service';
import { UtilsService } from '../utils/utils.service';
import { Position } from '../../models/position';

/** GPS drift from the last range origin that triggers a fresh range calculation. */
const RANGE_RECALC_DISTANCE_M = 2000;
import { WidgetService } from '../platform/widget.service';
import { AnalyticsLoggerService } from '../logging/analytics-logger.service';

/**
 * MainComponent implements this interface and binds itself to the handler in
 * ngAfterViewInit. The handler reads UI state (navigating, showing-map),
 * writes a few UI flags (loadingMap, failedPlan, navigationRecalculating),
 * and delegates a small number of operations that depend on ViewChildren
 * (wind panel, mobile panel routing, feedback modal) back to the host.
 *
 * The handler does NOT own the host's lifecycle — MainComponent's
 * ngOnDestroy must call `unbind()` to drop the reference.
 */
export interface CalculationFlowHost {
  readonly mapComponent?: MapComponent;
  readonly vehiclePanelComponent: VehiclePanelComponent;

  readonly isShowingMap: boolean;
  readonly isNavigating: WritableSignal<boolean>;
  readonly isNavigationRecalculating: WritableSignal<boolean>;
  readonly failedPlan: WritableSignal<boolean>;
  readonly loadingMap: WritableSignal<string | null>;

  openFeedbackDialog(): void;
  updateWindPanel(wind: WindElement[], temperature: number, weatherCode: number): void;
  setMobileVisibleRoutePanel(panel: string): void;
}

/**
 * Owns the UI side of the calculation flow. Subscribes to
 * `EvCalculationService.state$` and dispatches pending / success / error
 * effects against a bound host (MainComponent).
 *
 * Split from MainComponent because the side-effect logic for state
 * transitions is large (~400 lines) and self-contained: it doesn't need
 * MainComponent's voice-command, lifecycle, or settings code, and keeping
 * it in MainComponent obscured the calculation flow.
 *
 * Also owns:
 *   - the 10-minute better-route check timer (started/stopped by host
 *     around navigation lifecycle);
 *   - the "user accepted a faster alternative" subscription on
 *     ObservableBetterRoute (Replan state → onRouteSuccess);
 *   - the feedback-counter logic that surfaces a satisfaction prompt after
 *     30 successful calculations.
 */
@Injectable({ providedIn: 'root' })
export class EvCalculationFlowHandler {
  private host: CalculationFlowHost | null = null;
  private stateSub: Subscription | null = null;
  private betterRouteAcceptSub: Subscription | null = null;
  private betterRouteTimer: Subscription | null = null;
  private locationSub: Subscription | null = null;
  private feedbackCounter: number = -1;
  /** Origin of the last successfully drawn range; GPS drift is measured from here. */
  private lastRangeStart: LatLng | null = null;

  private readonly evCalculationService = inject(EvCalculationService);
  private readonly waypointService = inject(WaypointService);
  private readonly mapService = inject(MapService);
  private readonly mapRangeDrawerService = inject(MapRangeDrawerService);
  private readonly mapRouteDrawerService = inject(MapRouteDrawerService);
  private readonly navigationMotionControllerService = inject(NavigationMotionControllerService);
  private readonly dataParserService = inject(DataParserService);
  private readonly webviewService = inject(WebviewService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly vehicleService = inject(VehicleService);
  private readonly locationService = inject(LocationService);
  private readonly utilsService = inject(UtilsService);
  private readonly injector = inject(Injector);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    const stored = localStorage.getItem('feedbackCounter');
    this.feedbackCounter = stored == null ? 0 : Number.parseInt(stored);
  }

  // NavigationService injects EvCalculationService injects this handler
  // (transitively through state$ subscription) — break cycle via Injector.
  private get navigationService(): NavigationService {
    return this.injector.get(NavigationService);
  }

  // AnalyticsLoggerService injects NavigationService: same cycle, same fix.
  private get analyticsLoggerService(): AnalyticsLoggerService {
    return this.injector.get(AnalyticsLoggerService);
  }

  bind(host: CalculationFlowHost): void {
    this.host = host;
    if (!this.stateSub) {
      this.stateSub = this.evCalculationService.state$.subscribe(state => this.handle(state));
    }
    if (!this.betterRouteAcceptSub) {
      this.betterRouteAcceptSub = this.navigationService.ObservableBetterRoute.subscribe(resp => {
        if (resp?.state === BetterRouteState.Replan) {
          this.applyRouteSuccess(resp.routeData, resp.rangeParams);
        }
      });
    }
    if (!this.locationSub) {
      this.locationSub = this.locationService.ObservableLastPosition.subscribe(position => {
        if (position) this.onGpsPosition(position);
      });
    }
  }

  unbind(): void {
    this.stateSub?.unsubscribe();
    this.stateSub = null;
    this.betterRouteAcceptSub?.unsubscribe();
    this.betterRouteAcceptSub = null;
    this.locationSub?.unsubscribe();
    this.locationSub = null;
    this.lastRangeStart = null;
    this.stopBetterRouteTimer();
    this.host = null;
  }

  /**
   * Range mode follows the GPS fix: once the user has moved more than
   * RANGE_RECALC_DISTANCE_M from the origin of the last drawn range, the start
   * waypoint is re-anchored to the current position, which feeds the
   * calculation pipeline and produces a fresh range. That calculation arrives
   * tagged GPS_DRIFT and is applied as a background refresh (no placeholder,
   * no re-framing — see onRangePending / onRangeSuccess). Skipped while a
   * calculation is in flight, while navigating or showing something on the
   * map, and when the user pinned a searched start point (follow mode off).
   */
  private onGpsPosition(position: Position): void {
    if (!this.host || !this.lastRangeStart) return;
    if (!Number.isFinite(position.Latitude) || !Number.isFinite(position.Longitude)) return;
    if (this.host.isNavigating() || this.host.isShowingMap) return;
    if (this.host.loadingMap() != null) return;
    if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.RANGE) return;
    if (this.host.mapComponent && !this.host.mapComponent.getFollowModeRange()) return;

    const drift = this.utilsService.distanceBetweenCoordinates(
      [this.lastRangeStart.lat, this.lastRangeStart.lng],
      [position.Latitude, position.Longitude],
    );
    if (drift <= RANGE_RECALC_DISTANCE_M) return;

    // Move the anchor right away so the fixes arriving during the pipeline's
    // debounce window don't queue up duplicate start updates.
    const start = new LatLng(position.Latitude, position.Longitude);
    this.lastRangeStart = start;
    this.evCalculationService.requestRangeRecalcFromGps(start);
  }

  startBetterRouteTimer(): void {
    this.stopBetterRouteTimer();
    this.betterRouteTimer = timer(600000, 600000).subscribe(() => {
      if (this.navigationService.getChargeState() === ChargeState.None) {
        this.evCalculationService.requestBetterRouteCheck();
      }
    });
  }

  stopBetterRouteTimer(): void {
    if (this.betterRouteTimer) {
      this.navigationService.ObservableBetterRoute.next(null);
      this.betterRouteTimer.unsubscribe();
      this.betterRouteTimer = null;
    }
  }

  /**
   * Public so MainComponent's better-route-accept subscriber can call it
   * even before bind() has run (e.g. accept popup arrives during a setup
   * race). Idempotent w.r.t. host being null — early-returns.
   */
  applyRouteSuccess(resp: any, rangeParams: RangeParams): void {
    this.onRouteSuccess(resp, rangeParams);
  }

  // ============= state dispatch =============

  private handle(state: CalculationState): void {
    if (!this.host) return;
    if (state.status === 'idle') return;

    if (this.host.isShowingMap) {
      // Active showOnMap session — stop any range animation that pending
      // started, but otherwise ignore the calculation event.
      if (state.status === 'pending' && state.kind === 'range') {
        this.mapRangeDrawerService.stopLoadingAnimation();
      }
      return;
    }

    switch (state.status) {
      case 'pending':
        if (state.kind === 'range') this.onRangePending(state.rangeParams, state.intent);
        else this.onRoutePending(state.rangeParams);
        return;
      case 'success':
        if (state.kind === 'range') this.onRangeSuccess(state.data, state.rangeParams, state.intent);
        else this.onRouteSuccess(state.data, state.rangeParams);
        return;
      case 'error':
        this.handleCalculationError(state.error, state.rangeParams, state.kind, state.intent);
        if (state.kind === 'route') this.routeCallEpilogue();
        return;
    }
  }

  // ============= pending =============

  private onRoutePending(rangeParams: RangeParams): void {
    if (!this.host) return;
    // While navigating, the current route stays on screen until the replan
    // lands, so there is nothing to lock the map for. loadingMap would put the
    // map behind .disableclick and freeze it for the whole request; the
    // navigation card shows its own recalculating status instead.
    if (!this.host.isNavigating()) {
      this.setLoadingMap('line');
      this.evCalculationService.setFailedPlan({ failedPlan: false, maxReachedWaypoint: 0 });
      if (this.host.mapComponent) {
        this.host.mapComponent.drawRoutePlanningAnimationToMap(
          rangeParams.Waypoints.map(el => new LatLng(el.lat, el.lng)),
        );
      }
    }
  }

  /**
   * Two flavours of range calculation:
   *
   * - GPS_DRIFT (the car moved): a background refresh. The range on screen
   *   stays until the new one replaces it and the camera is not touched, so
   *   the follow camera keeps tracking the cursor undisturbed.
   * - Anything else (battery, car, settings, a moved start point, the first
   *   calculation after load): the range is the subject. The old range is
   *   replaced by the pulsing placeholder and the map is framed on it. Outside
   *   the startup overview this also leaves follow mode, otherwise the follow
   *   camera would pull the view straight back onto the cursor; the user
   *   returns to following with the follow button.
   */
  private onRangePending(rangeParams: RangeParams, intent: CalculationIntent): void {
    if (!this.host) return;
    this.mapRouteDrawerService.clearRoute();
    this.setLoadingMap('circle');
    if (intent === CalculationIntent.GPS_DRIFT) return;

    if (this.host.mapComponent) {
      this.host.mapComponent.clearRange();
    }

    // Keep the camera off the cursor while the range is calculated and framed;
    // released a few seconds after the finished range is on screen.
    this.navigationMotionControllerService.beginRangeOverviewHold();
    if (!this.navigationMotionControllerService.isRangeOverviewHoldActive() && this.host.mapComponent) {
      this.host.mapComponent.setFollowModeState(FollowModeState.PANNED);
    }

    const startWp = rangeParams.Waypoints[0];
    if (startWp) {
      const selectedCar = this.vehicleService.getSelectedCar();
      const soh = rangeParams.RangeInputParams.batteryStateOfHealth || 100;
      // Nominal remaining range. The drawer shortens it for the road-vs-circle
      // difference — don't pre-shrink it here as well.
      const estimatedRangeKm = selectedCar.FactoryRange * soh / 100 * rangeParams.RangeInputParams.batteryLevel / 100;
      this.mapRangeDrawerService.startLoadingAnimation([startWp.lng, startWp.lat], estimatedRangeKm, this.rangeFit());
    }
  }

  /**
   * The startup overview opens the page on the range instead of flying out
   * to it from the initial map view; later re-framings animate.
   */
  private rangeFit(): RangeFit {
    return this.navigationMotionControllerService.isRangeOverviewHoldActive() ? 'instant' : 'animate';
  }

  // ============= success =============

  private onRouteSuccess(resp: any, rangeParams: RangeParams): void {
    if (!this.host) return;
    if (this.host.isShowingMap) return;

    this.setLoadingMap(null);
    this.host.failedPlan.set(false);
    this.evCalculationService.setFailedPlan({ failedPlan: false, maxReachedWaypoint: 0 });
    this.waypointService.persistToStorage();
    const routeData: Route = this.dataParserService.parseRouteData(resp);

    if (this.waypointService.getSearchedRangeOrRoute() === false) {
      for (let i = 0; i < routeData.WaypointsAddresses.length; i++) {
        routeData.WaypointsAddresses[i].Location = rangeParams.Waypoints[i];
      }
      this.waypointService.setReverseGeocodedLocations(routeData.WaypointsAddresses);
    }

    this.mapService.setLastRouteData(routeData);

    if (this.host.isNavigating()) {
      this.host.isNavigationRecalculating.set(false);
      // Lets the trip log flag replan_event / user_triggered_replan / battery_changed on the next fix.
      this.analyticsLoggerService.successfulReplanHttpCallEvent();
      if (this.host.mapComponent) {
        this.host.mapComponent.setPlannedRoute(routeData);
      }
    } else {
      if (this.host.mapComponent) {
        this.host.mapComponent.drawRouteToMap(routeData);
      }
      this.tickFeedbackCounter();
    }

    if (!this.host.isNavigating()) {
      if (this.host.mapComponent) {
        this.host.mapComponent.initWind(routeData.Wind);
      }
    }
    this.host.updateWindPanel(routeData.Wind, routeData.Temperature, routeData.WeatherCode);

    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.RouteUpdate(JSON.stringify(resp));
    }
    this.routeCallEpilogue();
  }

  private onRangeSuccess(resp: any, rangeParams: RangeParams, intent: CalculationIntent): void {
    if (!this.host) return;
    // Only sync the server-returned SOC back if the battery level has not
    // changed since this request was sent; otherwise the stale response
    // would clobber the user's newer stepper/slider input.
    const sentBattery = rangeParams.RangeInputParams?.batteryLevel;
    const currentBattery = this.vehicleService.getECarSettings()?.batteryLevel;
    if (sentBattery === currentBattery) {
      this.vehicleService.setBatterySilent(resp.SOC);
      this.vehicleService.ObservableBatteryChanged.next(resp.SOC);
      this.host.vehiclePanelComponent.setbatteryLevel(resp.SOC);
    }
    const rangeData: Range = this.dataParserService.parseRangeData(resp);

    const startWp = rangeParams.Waypoints[0];
    this.lastRangeStart = startWp ? new LatLng(startWp.lat, startWp.lng) : null;

    this.setLoadingMap(null);
    this.tickFeedbackCounter();
    this.waypointService.persistToStorage();
    // A GPS_DRIFT refresh swaps the blob in place and leaves the camera alone;
    // every other calculation frames the new range.
    const isBackgroundRefresh = intent === CalculationIntent.GPS_DRIFT;
    if (this.host.mapComponent) {
      // The wind animation plays while the new range is framed; a background refresh plays none
      this.host.mapComponent.initWind(rangeData.Wind, !isBackgroundRefresh);
    }
    // The pipeline runs during route resolution, so the first calculation can
    // already have succeeded by the time this handler is bound and its pending
    // state was never seen here. Start the startup overview from the success
    // then; a no-op once the overview has played.
    if (!isBackgroundRefresh) this.navigationMotionControllerService.beginRangeOverviewHold();
    this.mapRangeDrawerService.drawRange(rangeData, isBackgroundRefresh ? 'none' : this.rangeFit());
    if (!isBackgroundRefresh) {
      this.navigationMotionControllerService.scheduleRangeOverviewRelease();
      // Without a GPS fix nothing else ever places the cursor: mark the range
      // origin with it, so the map still shows where the range is measured from.
      if (!this.locationService.getLastPosition() && startWp && this.host.mapComponent) {
        this.host.mapComponent.placeCursorAt(startWp.lng, startWp.lat);
      }
    }
    this.host.updateWindPanel(rangeData.Wind, rangeData.Temperature, rangeData.WeatherCode);
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.RhaPolygonUpdate(JSON.stringify(resp));
    }
  }

  private routeCallEpilogue(): void {
    if (!this.host) return;
    this.navigationService.skipChargingStation = false;
    this.host.setMobileVisibleRoutePanel('routepoints');
  }

  // ============= error =============

  private handleCalculationError(
    err: CalculationError,
    rangeParams: RangeParams,
    ctx: 'route' | 'range',
    intent: CalculationIntent,
  ): void {
    if (!this.host) return;

    // A failed background refresh is not worth a dialog while driving: the
    // previous range stays on screen and the next RANGE_RECALC_DISTANCE_M of
    // travel retries it.
    if (ctx === 'range' && intent === CalculationIntent.GPS_DRIFT) {
      this.setLoadingMap(null);
      return;
    }

    // No range to frame — hand the camera back, but leave the overview available
    // so a successful retry still gets it.
    if (ctx === 'range') this.navigationMotionControllerService.abortRangeOverviewHold(false);

    const lang = this.languageService.languageJSON;

    switch (err.kind) {
      case 'route_limit_exceeded': {
        this.openErrorDialog(lang.App_ErrorDialog_RouteLimit, this.errorIconUrl('popup_icon_toofar.svg'));
        this.failRouteFromStart();
        return;
      }
      case 'free_tier_limit': {
        this.stackedModalsService.openModal(SubscriptionRequiredDialogComponent, { data: { feature: 'route-planning' }, autoFocus: false });
        this.failRouteFromStart();
        return;
      }
      case 'country_not_supported': {
        this.handleCountryNotSupported(ctx);
        return;
      }
      case 'closest_route_too_far': {
        this.mapRangeDrawerService.stopLoadingAnimation();
        this.setLoadingMap(null);
        this.openErrorDialog(lang.App_ErrorDialog_ClosestRouteTooFar, this.errorIconUrl('popup_icon_nomap.svg'));
        this.host.mapComponent?.clearMapLayers();
        this.notifyWebviewRangeCleared();
        return;
      }
      case 'cannot_calculate_route': {
        this.handleCannotCalculateRoute(err, rangeParams);
        return;
      }
      case 'cannot_calculate_range': {
        this.mapRangeDrawerService.stopLoadingAnimation();
        this.setLoadingMap(null);
        this.openErrorDialog(lang.App_ErrorDialog_CannotCalculateRange, this.errorIconUrl('popup_icon_nomap.svg'));
        if (err.source === 'invalid_response' && this.host.mapComponent) {
          this.host.mapComponent.clearMapLayers();
        }
        this.notifyWebviewRangeCleared();
        return;
      }
      case 'route_network': {
        // Nav-mode retry handled inside EvCalculationService via retry({ delay }).
        // This branch fires only outside navigation.
        this.setLoadingMap(null);
        this.markPlanFailed(0);
        this.openErrorDialog(lang.App_ErrorDialog_CannotCalculateRoute, this.errorIconUrl('popup_icon_nomap.svg'));
        this.drawFailedRoute(0);
        this.notifyWebviewRouteCleared();
        return;
      }
      case 'no_charger_types': {
        this.openErrorDialog(lang.App_ErrorDialog_SelectChargingStation, this.errorIconUrl('popup_icon_select_charging_station.svg'));
        if (ctx === 'range') {
          this.mapRangeDrawerService.stopLoadingAnimation();
          this.setLoadingMap(null);
        }
        return;
      }
    }
  }

  private handleCountryNotSupported(ctx: 'route' | 'range'): void {
    const title = this.languageService.languageJSON.App_ErrorDialog_CountryNotSupported;
    if (ctx === 'route') {
      this.setLoadingMap(null);
      this.markPlanFailed(0);
      this.openErrorDialog(title, this.errorIconUrl('popup_icon_nomap.svg'));
      this.trimRangeWaypoints();
      this.drawFailedRoute(0);
      this.notifyWebviewRouteCleared();
    } else {
      this.mapRangeDrawerService.stopLoadingAnimation();
      this.setLoadingMap(null);
      this.openErrorDialog(title, this.errorIconUrl('popup_icon_nomap.svg'));
      this.notifyWebviewRangeCleared();
    }
  }

  private handleCannotCalculateRoute(
    err: Extract<CalculationError, { kind: 'cannot_calculate_route' }>,
    rangeParams: RangeParams,
  ): void {
    if (!this.host) return;
    if (err.source === 'server' && this.host.isNavigating()) {
      this.handleRouteFailureWhileNavigating();
    } else {
      this.setLoadingMap(null);
      this.markPlanFailed(err.maxReachedWaypoint);
      this.openErrorDialog(this.languageService.languageJSON.App_ErrorDialog_CannotCalculateRoute, this.errorIconUrl('popup_icon_nomap.svg'));
      this.applyFailedWaypointsInfo(err, rangeParams);
      this.trimRangeWaypoints();
      this.drawFailedRoute(err.maxReachedWaypoint);
    }
    this.notifyWebviewRouteCleared();
  }

  private handleRouteFailureWhileNavigating(): void {
    if (this.navigationService.skipChargingStation) {
      this.openSkipChargingStationFailedDialog();
      localStorage.setItem('skippedChargingStation', JSON.stringify(null));
      this.navigationService.ObservableCannotSkipMoreChargingStation.next(true);
    } else {
      this.navigationService.ObservableChangeNavigation.next('exit');
    }
  }

  // Attaches the requested coordinates to the reverse-geocoded waypoint
  // addresses the failed response carried, and publishes them.
  private applyFailedWaypointsInfo(
    err: Extract<CalculationError, { kind: 'cannot_calculate_route' }>,
    rangeParams: RangeParams,
  ): void {
    if (this.waypointService.getSearchedRangeOrRoute() !== false || !err.waypointsInfo) return;
    for (let i = 0; i < err.waypointsInfo.length; i++) {
      (err.waypointsInfo[i] as WaypointAddress).Location = rangeParams.Waypoints[i];
    }
    if (!(err.source === 'server' && err.waypointsInfo.length === 0)) {
      this.waypointService.setReverseGeocodedLocations(err.waypointsInfo);
    }
  }

  // Shared tail of the errors that fail the whole route before its first leg.
  private failRouteFromStart(): void {
    this.markPlanFailed(0);
    this.setLoadingMap(null);
    this.trimRangeWaypoints();
    this.drawFailedRoute(0);
  }

  private markPlanFailed(maxReachedWaypoint: number): void {
    if (!this.host) return;
    this.host.failedPlan.set(true);
    this.evCalculationService.setFailedPlan({ failedPlan: true, maxReachedWaypoint });
    this.host.isNavigationRecalculating.set(false);
  }

  private trimRangeWaypoints(): void {
    if (this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE) {
      this.waypointService.setWaypointParams([this.waypointService.getWaypointsParams()[0]]);
    }
  }

  private drawFailedRoute(maxReachedWaypoint: number): void {
    this.host?.mapComponent?.drawFailedRouteToMap(maxReachedWaypoint);
  }

  private notifyWebviewRouteCleared(): void {
    if (this.webviewService.IsGPSTWebview()) this.webviewService.RouteUpdate('');
  }

  private notifyWebviewRangeCleared(): void {
    if (this.webviewService.IsGPSTWebview()) this.webviewService.RhaPolygonUpdate('');
  }

  private errorIconUrl(name: string): string {
    return `/assets/icons/${this.settingsService.getMap().Skin}/error/${name}`;
  }

  // ============= helpers =============

  private setLoadingMap(loading: string | null): void {
    if (!this.host) return;
    if (this.host.loadingMap() === 'line' && loading !== 'line' && !this.host.isNavigating() && this.host.mapComponent) {
      this.host.mapComponent.clearMapLayers();
    }
    this.host.loadingMap.set(loading);
  }

  private openErrorDialog(title: string, icon: string, message: string | null = null): void {
    if (!this.host) return;
    if ((!this.host.isNavigating() || this.host.isNavigationRecalculating()) &&
      document.getElementsByClassName('mat-dialog-title').length === 0) {
      const selectedDisplayMode = this.waypointService.getSelectedDisplayMode();
      const batteryLevel = this.vehicleService.getECarSettings()?.batteryLevel ?? 100;
      const war = selectedDisplayMode === DisplayMode.ROUTE && batteryLevel < 10
        ? this.languageService.languageJSON.App_WarningLowBattery : null;
      this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
        data: { title, icon, message, warning: war, buttons: [this.languageService.languageJSON.Global_Ok, null] },
        autoFocus: false,
      });

      if (this.host.isNavigating()) {
        this.navigationService.ObservableChangeNavigation.next('exit');
      }
    }
  }

  private openSkipChargingStationFailedDialog(): void {
    this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.Global_Alert,
        message: this.languageService.languageJSON.App_CannotSkipChargingation,
        icon: `/assets/icons/${this.settingsService.getMap().Skin}/error/skip_chargingstation_image.svg`,
        buttons: [this.languageService.languageJSON.Global_Ok, null],
      },
      autoFocus: false,
    });
  }

  private tickFeedbackCounter(): void {
    if (!this.host || this.widgetService.isWidget()) return;
    if (this.feedbackCounter === -1 || this.feedbackCounter >= 30) return;
    this.feedbackCounter++;
    localStorage.setItem('feedbackCounter', this.feedbackCounter.toString());
    if (this.feedbackCounter !== 30) return;

    timer(10000).pipe(first()).subscribe(() => {
      if (!this.host) return;
      if (this.host.isNavigating()) {
        // Defer past navigation — don't bother the user during driving.
        this.feedbackCounter--;
      } else {
        this.host?.openFeedbackDialog();
      }
    });
  }
}
